import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import type { ChargerDetailModel, ChargerModel } from '../api/client/chargers'
import type { ConnectorFormat, ConnectorPowerType, ConnectorType, FirmwareStatus } from '../types/chargers'
import { type CommissioningState, isConnectorFormat, isConnectorPowerType, isConnectorType } from '../types/chargers'

type DiagnosticStatus = NonNullable<ChargerModel['diagnosticStatus']>
type InstallationType = NonNullable<ChargerModel['evseInstallationType']>
type NetworkConnectionMethod = NonNullable<ChargerDetailModel['networkConnectionMethod']>
type ConnectorNameEntity = {
  connectorId?: number | string
  connectorName?: string
}

export type ConnectorNameFormatter = ReturnType<typeof useConnectorNameFormatter>

/**
 * Hook that provides a formatter for connector names.
 *
 * @returns A function that formats connector names. If a custom connector name
 * is provided, it returns that name. Otherwise, it returns a localized default
 * name like "Connector 1". Returns "Unknown Connector" if no connector ID is provided.
 */
export function useConnectorNameFormatter() {
  const t = useTranslations('data.connectors')
  return useCallback(
    (connector?: ConnectorNameEntity) => {
      const { connectorId, connectorName } = connector ?? {}
      if (!connectorId) return t('unknown')
      return connectorName || t('connectorName', { connectorId })
    },
    [t]
  )
}

/**
 * Hook that provides a formatter for charger diagnostic statuses.
 *
 * @returns A function that takes a diagnostic status and returns its
 * localized human-readable representation.
 */
export function useDiagnosticStatusFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((diagnosticStatus: DiagnosticStatus) => t(`diagnosticStatus_${diagnosticStatus}`), [t])
}

/**
 * Hook that provides a formatter for charger installation types.
 *
 * @returns A function that takes an installation type (e.g., "Pedestal", "WallMounted", "Other")
 * and returns its localized human-readable representation.
 */
export function useInstallationTypeFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((installationType: InstallationType) => t(`metadata_installationType_${installationType}`), [t])
}

/**
 * Hook that provides a formatter for network connection methods.
 *
 * @returns A function that takes a network connection method (e.g., "WiFi", "LAN", "SIM")
 * and returns its localized human-readable representation.
 */
export function useNetworkConnectionMethodFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((networkConnectionMethod: NetworkConnectionMethod) => t(`metadata_networkConnectionMethod_${networkConnectionMethod}`), [t])
}

/**
 * Hook that provides a formatter for charger commissioning states.
 *
 * @returns A function that takes a commissioning state and returns its
 * localized human-readable representation. Handles "PRECOMMISSIONED", "COMMISSIONED",
 * "DECOMMISSIONED" states, and returns "Unknown" for any other value.
 */
export function useCommissioningStateFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback(
    (commissioningState?: CommissioningState) => {
      switch (commissioningState) {
        case 'PRECOMMISSIONED':
        case 'COMMISSIONED':
        case 'DECOMMISSIONED':
          return t(`metadata_commissioningState_${commissioningState}`)
        default:
          return t('metadata_commissioningState_UNKNOWN')
      }
    },
    [t]
  )
}

/**
 * Hook that provides a formatter for ADA accessibility status.
 *
 * @returns A function that takes a boolean ADA accessibility value and returns
 * its localized human-readable representation ("Yes" or "No").
 */
export function useAdaAccessibleFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((isAdaAccessible: boolean) => (isAdaAccessible ? t('metadata_adaAccessible_yes') : t('metadata_adaAccessible_no')), [t])
}

// Function overloads to provide conditional return types
type ConnectorTypeFormatter = {
  (connectorType: ConnectorType): string
  (connectorType: string): string | undefined
}

export function useConnectorTypeFormatter() {
  const t = useTranslations('data.connectors')
  return useCallback(
    (connectorType: string) => {
      if (isConnectorType(connectorType)) {
        return t(`types_${connectorType}`)
      }
    },
    [t]
  ) as ConnectorTypeFormatter
}

type ConnectorFormatFormatter = {
  (connectorFormat: ConnectorFormat): string
  (connectorFormat: string): string | undefined
}

export function useConnectorFormatFormatter() {
  const t = useTranslations('data.connectors')
  return useCallback(
    (connectorFormat: string) => {
      if (isConnectorFormat(connectorFormat)) {
        return t(`formats_${connectorFormat}`)
      }
    },
    [t]
  ) as ConnectorFormatFormatter
}

type ConnectorPowerTypeFormatter = {
  (connectorPowerType: ConnectorPowerType): string
  (connectorPowerType: string): string | undefined
}

export function useConnectorPowerTypeFormatter() {
  const t = useTranslations('data.connectors')
  return useCallback(
    (connectorPowerType: string) => {
      if (isConnectorPowerType(connectorPowerType)) {
        return t(`powerTypes_${connectorPowerType}`)
      }
    },
    [t]
  ) as ConnectorPowerTypeFormatter
}

export function useFirmwareStatusFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback((firmwareStatus: FirmwareStatus) => t(`firmwareStatus_${firmwareStatus}`), [t])
}
